import React, { useEffect, useRef, useState } from 'react';
import {
  Play, ArrowRight, CheckCircle, ChevronDown, FileText, Download,
  Star, Users, Clock, BookOpen, Award, Lock,
} from 'lucide-react';
import { G1, GG } from '../../config/styles';
import { VideoPlayer } from '../shared';

// ── ألوان الهوية ──────────────────────────────────────────
const PURPLE = '#667eea';
const PURPLE2 = '#764ba2';
const GREEN = '#10b981';
const INK = '#1F2937';
const MUTED = '#6B7280';

export function Player({
  activeCourse, activeLesson, setActiveLesson,
  lessons, lang, isRTL, t, onBack, onBackHome,
  customer, markWatched, isWatched,
  studentWorks = [], reviews = [], enrollments = [], instructors = [],
  isEnrolled = true, onEnroll, onOpenInstructor, onAddReview,
}) {
  const watchTimer = useRef(null);
  const [openId, setOpenId] = useState(null);
  const [lightbox, setLightbox] = useState(null); // صورة العمل المفتوحة في عارض مدمج
  // فورم إضافة رأي
  const [myRating, setMyRating]   = useState(0);
  const [myComment, setMyComment] = useState('');
  const [reviewSent, setReviewSent] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);

  const ar = lang === 'ar';
  const cLessons = lessons
    .filter(l => l.course_id === activeCourse.id)
    .sort((a, b) => a.order_num - b.order_num);

  const cWorks   = studentWorks.filter(w => w.course_id === activeCourse.id && w.is_active !== false)
    .sort((a, b) => (a.order_num || 0) - (b.order_num || 0));
  const cReviews = reviews.filter(r => r.course_id === activeCourse.id && r.is_approved !== false)
    .sort((a, b) => (a.order_num || 0) - (b.order_num || 0));

  const watchedCount = cLessons.filter(l => isWatched(l.id)).length;
  const progressPct  = cLessons.length ? Math.round((watchedCount / cLessons.length) * 100) : 0;

  const courseName = ar ? activeCourse.name_ar : activeCourse.name_en;
  const courseDesc = ar ? activeCourse.description_ar : activeCourse.description_en;
  const features   = Array.isArray(activeCourse.features) ? activeCourse.features : [];

  // عدد الطلاب الفعلي = المشتركين النشطين + الرقم الدعائي
  const realStudents = enrollments.filter(e => e.course_id === activeCourse.id && e.status === 'active').length;
  const shownStudents = realStudents + (Number(activeCourse.bonus_students) || 0);

  // التقييم الفعلي = متوسط آراء الطلاب
  const avgRating = cReviews.length
    ? (cReviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / cReviews.length)
    : 0;

  // هل الطالب ضاف رأي قبل كده؟ (معتمد أو مستني موافقة)
  const alreadyReviewed = customer && !customer.isGuest &&
    reviews.some(r => r.course_id === activeCourse.id && r.customer_id === customer.id);

  const submitReview = async () => {
    if (!myRating) return;
    if (!isEnrolled) { alert(ar ? 'لازم تشترك في الكورس الأول' : 'Enroll first'); return; }
    setReviewBusy(true);
    // حماية: لو الإرسال اتأخر، نوقف التحميل
    const safety = setTimeout(() => setReviewBusy(false), 12000);
    try {
      const res = await onAddReview({
        course_id: activeCourse.id,
        customer_id: customer.id,
        student_name: customer.name || (ar ? 'طالب' : 'Student'),
        rating: myRating,
        comment_ar: myComment.trim() || null,
      });
      clearTimeout(safety);
      if (!res?.error) { setReviewSent(true); setMyRating(0); setMyComment(''); }
      else { console.error('Review error:', res.error); alert(ar ? 'حصل خطأ، حاول تاني' : 'Error, try again'); }
    } catch (e) {
      clearTimeout(safety);
      console.error('Review submit failed:', e);
      alert(ar ? 'حصل خطأ، حاول تاني' : 'Error, try again');
    } finally {
      setReviewBusy(false);
    }
  };

  // أول ما تفتح حلقة، نعتبرها اتشافت بعد 30 ثانية
  useEffect(() => {
    if (!openId || !customer?.id) return;
    if (isWatched(openId)) return;
    clearTimeout(watchTimer.current);
    watchTimer.current = setTimeout(() => {
      markWatched(customer.id, openId, activeCourse.id);
    }, 30000);
    return () => clearTimeout(watchTimer.current);
  }, [openId]);

  const toggleLesson = (lesson) => {
    const locked = !isEnrolled && !lesson.is_free;
    if (locked) return;
    const next = openId === lesson.id ? null : lesson.id;
    setOpenId(next);
    setActiveLesson(next ? lesson : null);
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', paddingBottom: '40px' }}>

      {/* ───────── أزرار الرجوع ───────── */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap' }}>
        <button onClick={onBack}
          style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', padding: '8px 14px', color: 'white', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <ArrowRight size={15} style={{ transform: isRTL ? 'none' : 'rotate(180deg)' }} /> {ar ? 'رجوع للكورسات' : 'Back to Courses'}
        </button>
        <button onClick={onBackHome}
          style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '10px', padding: '8px 14px', color: 'white', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          🏠 {ar ? 'الرئيسية' : 'Home'}
        </button>
      </div>

      {/* ═════════ 1) هيدر الكورس ═════════ */}
      <div style={{ background: 'white', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', marginBottom: '24px' }}>

        {/* بانر */}
        <div style={{ position: 'relative', background: `linear-gradient(135deg,${PURPLE},${PURPLE2})`, minHeight: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {activeCourse.banner_url
            ? <img src={activeCourse.banner_url} alt={courseName} style={{ width: '100%', maxHeight: '320px', objectFit: 'cover', display: 'block' }} />
            : <div style={{ fontSize: '72px', padding: '40px' }}>{activeCourse.icon || '🎓'}</div>}
          {isEnrolled && (
            <span style={{ position: 'absolute', top: '14px', insetInlineEnd: '14px', background: GG, color: 'white', fontSize: '12px', fontWeight: 'bold', padding: '5px 12px', borderRadius: '20px', boxShadow: '0 4px 12px rgba(16,185,129,0.4)' }}>
              {t.enrolled || 'مشترك ✓'}
            </span>
          )}
        </div>

        {/* تفاصيل الهيدر */}
        <div style={{ padding: '24px' }}>
          <h1 style={{ fontSize: '26px', fontWeight: 800, color: INK, margin: '0 0 14px', lineHeight: 1.35 }}>{courseName}</h1>

          {/* صف الميتا: تقييم + طلاب + حلقات + مواعيد */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '18px', alignItems: 'center', marginBottom: '20px', fontSize: '13px', color: MUTED }}>
            {/* التقييم — يظهر دايمًا */}
            {avgRating > 0 ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#FFFBEB', padding: '5px 12px', borderRadius: '20px', border: '1px solid #FDE68A' }}>
                <span style={{ display: 'flex', gap: '1px' }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <Star key={n} size={15} fill={n <= Math.round(avgRating) ? '#F59E0B' : 'none'} color={n <= Math.round(avgRating) ? '#F59E0B' : '#FCD34D'} />
                  ))}
                </span>
                <b style={{ color: '#B45309', fontSize: '15px' }}>{avgRating.toFixed(1)}</b>
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#EEF2FF', padding: '5px 12px', borderRadius: '20px', color: '#667eea', fontWeight: 'bold', fontSize: '12px' }}>
                <Star size={14} color="#667eea" /> {t.newCourseBadge}
              </span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Users size={15} color={PURPLE} /> {shownStudents} {t.studentsWord}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <BookOpen size={15} color={PURPLE} /> {cLessons.length} {t.lessonWord}
            </span>
            {activeCourse.schedule_text_ar && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Clock size={15} color={PURPLE} /> {activeCourse.schedule_text_ar}
              </span>
            )}
          </div>

          {/* السعر + زر الاشتراك (يظهر بس لو مش مشترك) */}
          {!isEnrolled && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '14px', padding: '18px', background: '#F9FAFB', borderRadius: '14px', border: '1px solid #F3F4F6' }}>
              <div style={{ fontSize: '28px', fontWeight: 800, color: PURPLE }}>
                {activeCourse.price} <span style={{ fontSize: '15px', color: MUTED, fontWeight: 600 }}>{ar ? 'ج.م' : 'EGP'}</span>
              </div>
              <button onClick={onEnroll}
                style={{ background: `linear-gradient(135deg,${PURPLE},${PURPLE2})`, color: 'white', border: 'none', borderRadius: '12px', padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 8px 20px rgba(102,126,234,0.4)', transition: 'transform .15s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                {ar ? 'أنشئ حساب واشترك' : 'Enroll Now'}
              </button>
            </div>
          )}

          {/* المميزات */}
          {features.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '10px', marginTop: '18px' }}>
              {features.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                  <CheckCircle size={16} color={GREEN} style={{ flexShrink: 0 }} />
                  {typeof f === 'string' ? f : (ar ? f.ar : f.en) || f.ar || f.en}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* شريط نسبة الإكمال (للمشترك فقط) */}
      {isEnrolled && cLessons.length > 0 && (
        <Section icon={<Award size={18} color={PURPLE} />} title={ar ? 'تقدّمك في الكورس' : 'Your Progress'}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ flex: 1, height: '10px', background: '#E5E7EB', borderRadius: '20px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${progressPct}%`, background: GG, borderRadius: '20px', transition: 'width .5s ease' }} />
            </div>
            <span style={{ fontSize: '14px', fontWeight: 'bold', color: INK, minWidth: '90px', textAlign: 'center' }}>
              {watchedCount}/{cLessons.length} ({progressPct}%)
            </span>
          </div>
        </Section>
      )}

      {/* ═════════ 2) عن الكورس ═════════ */}
      {courseDesc && (
        <Section icon={<BookOpen size={18} color={PURPLE} />} title={ar ? 'عن الكورس' : 'About the Course'}>
          <p style={{ fontSize: '15px', lineHeight: 1.9, color: '#374151', margin: 0, whiteSpace: 'pre-line' }}>{courseDesc}</p>
        </Section>
      )}

      {/* ═════════ 3) محتوى الكورس (أكورديون) ═════════ */}
      <Section icon={<Play size={18} color={PURPLE} />}
        title={t.courseContent || (ar ? 'محتوى الكورس' : 'Course Content')}
        right={<span style={{ fontSize: '13px', color: MUTED }}>{cLessons.length} {t.lessonWord}</span>}>
        {cLessons.length === 0 ? (
          <Empty text={ar ? 'لا يوجد محتوى متاح بعد.' : 'No content available yet.'} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {cLessons.map((lesson, idx) => {
              const open    = openId === lesson.id;
              const watched = isWatched(lesson.id);
              const locked  = !isEnrolled && !lesson.is_free;
              const lDesc   = ar ? lesson.description_ar : lesson.description_en;
              const atts    = Array.isArray(lesson.attachments) ? lesson.attachments : [];

              return (
                <div key={lesson.id} style={{ border: `1px solid ${open ? PURPLE : '#E5E7EB'}`, borderRadius: '14px', overflow: 'hidden', background: 'white', transition: 'border-color .2s' }}>

                  {/* رأس الحلقة */}
                  <div onClick={() => toggleLesson(lesson)}
                    style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: locked ? 'not-allowed' : 'pointer', background: open ? '#EEF2FF' : 'white', opacity: locked ? 0.6 : 1, transition: 'background .15s' }}>

                    {/* أيقونة الترقيم/الحالة */}
                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: watched ? GG : open ? `linear-gradient(135deg,${PURPLE},${PURPLE2})` : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {locked ? <Lock size={15} color={MUTED} />
                        : watched ? <CheckCircle size={17} color="white" />
                        : open ? <Play size={13} color="white" />
                        : <span style={{ fontSize: '13px', fontWeight: 'bold', color: MUTED }}>{idx + 1}</span>}
                    </div>

                    {/* العنوان + المدة */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: open ? PURPLE : watched ? '#059669' : INK }}>
                        {ar ? lesson.title_ar : lesson.title_en}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', marginTop: '3px', fontSize: '11px', color: '#9CA3AF' }}>
                        {lesson.duration && <span>⏱ {lesson.duration}</span>}
                        {lesson.is_free && <span style={{ color: GREEN, fontWeight: 'bold' }}>{t.free}</span>}
                        {atts.length > 0 && <span>📎 {atts.length} {ar ? 'مرفق' : 'files'}</span>}
                      </div>
                    </div>

                    {/* سهم الفتح */}
                    {!locked && <ChevronDown size={18} color={MUTED} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .25s', flexShrink: 0 }} />}
                  </div>

                  {/* جسم الحلقة المفتوحة */}
                  {open && !locked && (
                    <div style={{ padding: '0 16px 18px', borderTop: '1px solid #F3F4F6' }}>
                      <div style={{ marginTop: '14px' }}>
                        <VideoPlayer lesson={lesson} />
                      </div>

                      {/* شرح الحلقة */}
                      {lDesc && (
                        <p style={{ fontSize: '14px', lineHeight: 1.8, color: '#374151', margin: '14px 0 0', whiteSpace: 'pre-line' }}>{lDesc}</p>
                      )}

                      {/* المرفقات */}
                      {atts.length > 0 && (
                        <div style={{ marginTop: '14px' }}>
                          <div style={{ fontSize: '13px', fontWeight: 'bold', color: INK, marginBottom: '8px' }}>📎 {ar ? 'مرفقات الحلقة' : 'Attachments'}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {atts.map((a, i) => (
                              <a key={i} href={a.url} target="_blank" rel="noreferrer"
                                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#F9FAFB', borderRadius: '10px', textDecoration: 'none', color: '#374151', fontSize: '13px', border: '1px solid #F3F4F6', transition: 'background .15s' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#EEF2FF'}
                                onMouseLeave={e => e.currentTarget.style.background = '#F9FAFB'}>
                                <FileText size={16} color={PURPLE} style={{ flexShrink: 0 }} />
                                <span style={{ flex: 1 }}>{a.name || (ar ? 'ملف مرفق' : 'Attachment')}</span>
                                <Download size={15} color={MUTED} />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* زر تأكيد المشاهدة */}
                      {customer && !watched && (
                        <button onClick={() => markWatched(customer.id, lesson.id, activeCourse.id)}
                          style={{ marginTop: '14px', padding: '9px 16px', background: '#F0FDF4', border: `1px solid ${GREEN}`, borderRadius: '10px', color: '#059669', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <CheckCircle size={15} /> {ar ? 'تم المشاهدة ✓' : 'Mark as Watched ✓'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* ═════════ 4) المدرب ═════════ */}
      {(() => {
        const linked = instructors.find(i => i.id === activeCourse.instructor_id);
        const insName   = linked ? linked.name_ar     : activeCourse.instructor_name;
        const insAvatar = linked ? linked.avatar_url   : activeCourse.instructor_avatar;
        const insBio    = linked ? (linked.bio_ar || linked.title_ar) : activeCourse.instructor_bio;
        if (!insName) return null;
        const clickable = !!linked && onOpenInstructor;
        return (
        <Section icon={<Users size={18} color={PURPLE} />} title={ar ? 'المدرب' : 'Instructor'}>
          <div onClick={() => clickable && onOpenInstructor(linked.id)}
            style={{ display: 'flex', alignItems: 'center', gap: '16px', cursor: clickable ? 'pointer' : 'default', padding: clickable ? '6px' : 0, borderRadius: '12px', transition: 'background .15s' }}
            onMouseEnter={e => { if (clickable) e.currentTarget.style.background = '#F9FAFB'; }}
            onMouseLeave={e => { if (clickable) e.currentTarget.style.background = 'transparent'; }}>
            {insAvatar
              ? <img src={insAvatar} alt={insName} style={{ width: '64px', height: '64px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `2px solid ${PURPLE}` }} />
              : <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: `linear-gradient(135deg,${PURPLE},${PURPLE2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '24px' }}>👨‍🏫</div>}
            <div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: clickable ? PURPLE : INK }}>{insName} {clickable && <span style={{ fontSize: '12px' }}>›</span>}</div>
              {insBio && <div style={{ fontSize: '13px', color: MUTED, marginTop: '4px', lineHeight: 1.7 }}>{insBio}</div>}
            </div>
          </div>
        </Section>
        );
      })()}

      {/* ═════════ 5) أعمال طلاب الكورس ═════════ */}
      <Section icon={<Play size={18} color={PURPLE} />} title={t.studentWorksTitle}>
        {cWorks.length === 0 ? (
          <Empty text={t.noStudentWorks} icon="🎬" />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: '14px' }}>
            {cWorks.map((w) => (
              <div key={w.id} style={{ borderRadius: '12px', overflow: 'hidden', background: '#F9FAFB', border: '1px solid #F3F4F6' }}>
                {w.media_type === 'image'
                  ? (
                    <div onClick={() => setLightbox(w)} onContextMenu={e => e.preventDefault()}
                      style={{ position: 'relative', cursor: 'zoom-in', height: '140px', overflow: 'hidden' }}>
                      <img src={w.media_url} alt={w.title_ar} draggable={false}
                        style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block', pointerEvents: 'none', userSelect: 'none' }} />
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', transition: 'background .2s', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.25)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}>
                        <span style={{ color: 'white', fontSize: '22px', opacity: 0, transition: 'opacity .2s' }} className="zoom-ico">🔍</span>
                      </div>
                    </div>
                  )
                  : <div style={{ height: '140px' }}><SafeWorkVideo url={w.media_url} /></div>}
                <div style={{ padding: '10px 12px' }}>
                  {w.title_ar && <div style={{ fontSize: '13px', fontWeight: 'bold', color: INK }}>{w.title_ar}</div>}
                  {w.student_name && <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>👤 {w.student_name}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* عارض الصور المدمج (Lightbox) — بدون رابط خارجي */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} onContextMenu={e => e.preventDefault()}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', animation: 'fadeIn .2s' }}>
          <button onClick={() => setLightbox(null)}
            style={{ position: 'absolute', top: '18px', [isRTL ? 'left' : 'right']: '18px', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', fontSize: '22px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: '92vw', maxHeight: '88vh', textAlign: 'center' }}>
            <img src={lightbox.media_url} alt={lightbox.title_ar} draggable={false} onContextMenu={e => e.preventDefault()}
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '12px', userSelect: 'none', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} />
            {(lightbox.title_ar || lightbox.student_name) && (
              <div style={{ color: 'white', marginTop: '14px' }}>
                {lightbox.title_ar && <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{lightbox.title_ar}</div>}
                {lightbox.student_name && <div style={{ fontSize: '13px', opacity: 0.7, marginTop: '3px' }}>👤 {lightbox.student_name}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═════════ 6) آراء الطلاب ═════════ */}
      <Section icon={<Star size={18} color={PURPLE} />} title={t.reviewsTitle}>

        {/* فورم إضافة رأي — للطالب المشترك في الكورس فقط */}
        {customer && !customer.isGuest && onAddReview && (
          !isEnrolled ? (
            <div style={{ padding: '14px 16px', background: '#FFFBEB', borderRadius: '12px', border: '1px solid #FDE68A', marginBottom: '16px', fontSize: '13px', color: '#B45309', textAlign: 'center', fontWeight: 'bold' }}>
              {t.enrollFirstReview}
            </div>
          ) : reviewSent || alreadyReviewed ? (
            <div style={{ padding: '14px 16px', background: '#F0FDF4', borderRadius: '12px', border: '1px solid #BBF7D0', marginBottom: '16px', fontSize: '13px', color: '#059669', textAlign: 'center', fontWeight: 'bold' }}>
              {reviewSent
                ? t.reviewPending
                : (t.alreadyReviewed)}
            </div>
          ) : (
            <div style={{ padding: '16px', background: '#F9FAFB', borderRadius: '12px', border: '1px solid #E5E7EB', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 'bold', color: INK, marginBottom: '10px' }}>{t.rateCourse}</div>
              {/* النجوم */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" onClick={() => setMyRating(n)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px' }}>
                    <Star size={26} fill={n <= myRating ? '#F59E0B' : 'none'} color={n <= myRating ? '#F59E0B' : '#D1D5DB'} />
                  </button>
                ))}
              </div>
              <textarea value={myComment} onChange={e => setMyComment(e.target.value)}
                placeholder={ar ? 'اكتب رأيك في الكورس (اختياري)...' : 'Write your review (optional)...'}
                style={{ width: '100%', minHeight: '70px', padding: '10px', borderRadius: '8px', border: '2px solid #E5E7EB', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }} />
              <button onClick={submitReview} disabled={!myRating || reviewBusy}
                style={{ marginTop: '10px', padding: '10px 22px', background: (!myRating || reviewBusy) ? '#9CA3AF' : `linear-gradient(135deg,${PURPLE},${PURPLE2})`, color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: (!myRating || reviewBusy) ? 'not-allowed' : 'pointer' }}>
                {reviewBusy ? (ar ? 'جاري الإرسال...' : 'Sending...') : (ar ? 'إرسال الرأي' : 'Submit Review')}
              </button>
            </div>
          )
        )}

        {cReviews.length === 0 ? (
          <Empty text={ar ? 'لا توجد مراجعات بعد.' : 'No reviews yet.'} icon="⭐" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {cReviews.map((r) => (
              <div key={r.id} style={{ padding: '14px 16px', background: '#F9FAFB', borderRadius: '12px', border: '1px solid #F3F4F6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  {r.avatar_url
                    ? <img src={r.avatar_url} alt={r.student_name} style={{ width: '38px', height: '38px', borderRadius: '50%', objectFit: 'cover' }} />
                    : <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: `linear-gradient(135deg,${PURPLE},${PURPLE2})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '15px', fontWeight: 'bold' }}>
                        {(r.student_name || '؟').charAt(0)}
                      </div>}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: INK }}>{r.student_name || (ar ? 'طالب' : 'Student')}</div>
                    <div style={{ display: 'flex', gap: '1px', marginTop: '2px' }}>
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star key={n} size={13} fill={n <= r.rating ? '#F59E0B' : 'none'} color={n <= r.rating ? '#F59E0B' : '#D1D5DB'} />
                      ))}
                    </div>
                  </div>
                </div>
                {r.comment_ar && <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7, margin: 0 }}>{r.comment_ar}</p>}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

// ── مكوّن قسم موحّد ──────────────────────────────────────
function Section({ icon, title, right, children }) {
  return (
    <div style={{ background: 'white', borderRadius: '18px', padding: '20px 22px', marginBottom: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.12)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '16px' }}>
        {icon}
        <h2 style={{ fontSize: '18px', fontWeight: 800, color: INK, margin: 0, flex: 1 }}>{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

// ── حالة فاضية ───────────────────────────────────────────
function Empty({ text, icon }) {
  return (
    <div style={{ textAlign: 'center', padding: '32px 16px', color: '#9CA3AF' }}>
      {icon && <div style={{ fontSize: '36px', marginBottom: '8px', opacity: 0.5 }}>{icon}</div>}
      <div style={{ fontSize: '14px' }}>{text}</div>
    </div>
  );
}

// ── استخراج vimeo_id من رابط (لأعمال الطلاب) ─────────────
function extractVimeo(url) {
  if (!url) return null;
  const m = String(url).match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}

// مشغّل آمن لأعمال الطلاب — يخفي زر الفتح الخارجي ويمنع right-click
function SafeWorkVideo({ url }) {
  const raw = String(url || '').trim();
  if (!raw) return null;

  // Vimeo — آمن أصلاً (مفيش زر خارجي)، نخفي العنوان/البايلاين
  const vMatch = raw.match(/vimeo\.com\/(\d+(?:\/[a-f0-9]+)?)/i);
  if (vMatch) {
    const [numId, hash] = vMatch[1].split('/');
    const src = hash
      ? `https://player.vimeo.com/video/${numId}?h=${hash}&color=667eea&byline=0&portrait=0&title=0&dnt=1`
      : `https://player.vimeo.com/video/${numId}?color=667eea&byline=0&portrait=0&title=0&dnt=1`;
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: '#000', userSelect: 'none' }} onContextMenu={e => e.preventDefault()}>
        <iframe src={src} style={{ width: '100%', height: '100%', border: 'none' }} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="work" />
      </div>
    );
  }

  // YouTube — نخفي شعار/الفيديوهات المقترحة قدر الإمكان
  const yMatch = raw.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/i);
  if (yMatch) {
    const yid = yMatch[1];
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: '#000', userSelect: 'none' }} onContextMenu={e => e.preventDefault()}>
        <iframe src={`https://www.youtube-nocookie.com/embed/${yid}?rel=0&modestbranding=1`} style={{ width: '100%', height: '100%', border: 'none' }} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="work" />
        {/* غطاء فوق الزاوية اللي فيها زر يوتيوب/الفتح الخارجي */}
        <div style={{ position: 'absolute', top: 0, [/* RTL agnostic */ 'right']: 0, width: '120px', height: '46px', zIndex: 3 }} />
      </div>
    );
  }

  // Google Drive — نخفي ركن زر "فتح في نافذة جديدة" بغطاء شفاف
  const dMatch = raw.match(/\/d\/([a-zA-Z0-9_-]+)/);
  const driveId = dMatch ? dMatch[1] : (/^[a-zA-Z0-9_-]{20,}$/.test(raw) ? raw : null);
  if (driveId) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: '#000', userSelect: 'none' }} onContextMenu={e => e.preventDefault()}>
        <iframe src={`https://drive.google.com/file/d/${driveId}/preview`} style={{ width: '100%', height: '100%', border: 'none' }} allow="autoplay" allowFullScreen title="work" />
        {/* غطاء يخفي زر الفتح الخارجي (الركن العلوي) */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '46px', zIndex: 3, background: 'transparent' }} />
      </div>
    );
  }

  // أي رابط تاني — نعرضه كـ iframe عادي داخل الموقع
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: '#000', userSelect: 'none' }} onContextMenu={e => e.preventDefault()}>
      <iframe src={raw} style={{ width: '100%', height: '100%', border: 'none' }} allowFullScreen title="work" />
    </div>
  );
}
